import { useMachine } from '@xstate/compiled/react';
import Table, { TableCell, TableRow } from 'components/Table/Table';
import { curr, roundNumber } from 'helpers/curr';
import React, { ChangeEvent } from 'react';
import { toast } from 'react-toastify';
import {
  HeroIconTrash,
  HeroIconUpload,
  HeroIconTrendingDown,
  HeroIconTrendingUp,
  useWrappedUrqlMutation,
} from 'toolbox';
import HeroIconCheveronDown from 'toolbox/src/components/HeroIcons/HeroIconCheveronDown';
import HeroIconCheveronUp from 'toolbox/src/components/HeroIcons/HeroIconCheveronUp';
import { ConvertFunction } from 'toolbox/src/hooks/useFetchConversionRates';
import { useClientSidePagination } from 'toolbox/src/hooks/usePagination';
import { useClientSideSortedTable } from 'toolbox/src/hooks/useSortedTable';
import { CurrencyFlag } from 'utils/Flag';
import { DashboardCurrency } from '../dashboardFilters.machine';
import useDashboardData from '../hooks/useDashboardData';
import { updateModelBalancesMachine } from '../updateModelBalances.machine';
import { expandableRowLogicMachine } from './ExpandableRowLogic.machine';
import {
  GetCurrencyBookBankAccountsQuery,
  useGetCurrencyBookBankAccountsQuery,
} from './GetCurrencyBookBankAccounts.generated';
import { useUpdateModelBalancesMutation } from './UpdateModelBalances.generated';
import { useDeleteModelBalancesMutation } from './DeleteModelBalances.generated';
import { JcapReactSelect } from 'components/JcapReactSelect/JcapReactSelect';
import dayjs from 'dayjs';

const TableCurrencyBooks = ({
  currency_books,
  isLimitingCurrencyBooks,
  currency,
  convert,
  reportModelBalanceChanged,
  shouldUseModelBalances,
  currencyBookSelectChoice,
  changeCurrencyBookSelectChoice,
  currencyBookSelectOptions,
  businessUnitSelectChoice,
  changeBusinessUnitSelectChoice,
  businessUnitSelectOptions,
}: {
  currency_books: ReturnType<typeof useDashboardData>['currency_books'];
  isLimitingCurrencyBooks: boolean;
  currency: DashboardCurrency;
  convert: ConvertFunction;
  reportModelBalanceChanged: () => void;
  shouldUseModelBalances: boolean;
  currencyBookSelectChoice: number | string;
  changeCurrencyBookSelectChoice: (e: ChangeEvent<HTMLSelectElement>) => void;
  currencyBookSelectOptions: any[];
  businessUnitSelectChoice: number;
  changeBusinessUnitSelectChoice: (e: ChangeEvent<HTMLSelectElement>) => void;
  businessUnitSelectOptions: any[];
}) => {
  let key: 'aggr_balance_gbp' | 'aggr_balance_usd' | 'aggr_balance_eur' =
    'aggr_balance_gbp';

  if (currency === 'EUR') {
    key = 'aggr_balance_eur';
  } else if (currency === 'USD') {
    key = 'aggr_balance_usd';
  }

  const [state, send] = useMachine(expandableRowLogicMachine, {});

  const { onColumnClick, sortingState, sortedData } = useClientSideSortedTable(
    currency_books,
    {
      defaultOrderBy: 'Balance',
      sort: (a, b, sortingState) => {
        switch (sortingState.columnId) {
          case 'Business Unit': {
            if (sortingState.order === 'asc') {
              return b.business_unit_name.localeCompare(a.business_unit_name);
            } else {
              return a.business_unit_name.localeCompare(b.business_unit_name);
            }
          }
          case 'Currency Book': {
            if (sortingState.order === 'asc') {
              return b.name.localeCompare(a.name);
            } else {
              return a.name.localeCompare(b.name);
            }
          }
          case 'Balance': {
            if (sortingState.order === 'asc') {
              return a.aggr_balance - b.aggr_balance;
            } else {
              return b.aggr_balance - a.aggr_balance;
            }
          }
          case 'Change': {
            if (!a.aggr_balance_change) {
              return 1;
            } else if (!b.aggr_balance_change) {
              return -1;
            }
            if (sortingState.order === 'asc') {
              return a.aggr_balance_change - b.aggr_balance_change;
            } else {
              return b.aggr_balance_change - a.aggr_balance_change;
            }
          }
          case 'Interest': {
            if (!a.aggr_yield) {
              return 1;
            } else if (!b.aggr_yield) {
              return -1;
            }
            if (sortingState.order === 'asc') {
              return a.aggr_yield - b.aggr_yield;
            } else {
              return b.aggr_yield - a.aggr_yield;
            }
          }
          default:
            return 0;
        }
      },
    },
  );

  return (
    <>
      <Table
        highlightRow={sortedData.findIndex(
          (row) => row.id === state.context.rowIdOpen,
        )}
        gridTemplateColumns="140px 1fr 125px 125px 125px"
        data={sortedData}
        onColumnClick={onColumnClick}
        sortingState={sortingState}
        columns={[
          {
            id: 'Business Unit',
            Header: (
              <div>
                <p>Business Unit</p>
                <JcapReactSelect
                  height={20}
                  options={businessUnitSelectOptions}
                  className="w-full mt-2 text-2xs"
                  value={businessUnitSelectChoice}
                  valueAccessor={({ id }) => id}
                  onChange={changeBusinessUnitSelectChoice}
                  labelAccessor={({ name }) => name}
                />
              </div>
            ),
            accessor: ({ business_unit_name }, rowIndex) => {
              return (
                <button className="flex items-center py-1 pr-2 overflow-hidden text-xs select-none">
                  <div className="flex-grow text-left break-words">
                    {business_unit_name}
                  </div>
                </button>
              );
            },
          },
          {
            id: 'Currency Book',
            Header: (
              <div>
                <p>Currency Book</p>
                <JcapReactSelect
                  height={20}
                  options={currencyBookSelectOptions}
                  className="w-full mt-2 text-2xs"
                  value={currencyBookSelectChoice}
                  valueAccessor={({ id }) => id}
                  onChange={changeCurrencyBookSelectChoice}
                  labelAccessor={({ name }) => name}
                />
              </div>
            ),
            accessor: ({ currency_prefix, name, accounts, id }, rowIndex) => (
              <div className="flex items-center w-full overflow-x-hidden select-none">
                <CurrencyFlag
                  currencyCode={currency_prefix}
                  className="inline w-8 h-auto mr-1 rounded-sm"
                />
                <div className="flex-grow overflow-x-hidden">
                  <div className="pl-2 font-semibold truncate">{name}</div>
                  <div
                    className="flex items-center px-2 text-xs font-semibold rounded cursor-pointer text-jcap-500 hover:bg-jcap-100"
                    onClick={() =>
                      send({
                        type: 'PRESS_ROW_EXPAND_BUTTON',
                        id,
                      })
                    }
                  >
                    {accounts} account{accounts > 1 && 's'}
                    {id === state.context.rowIdOpen ? (
                      <HeroIconCheveronUp className="inline w-6 h-6" />
                    ) : (
                      <HeroIconCheveronDown className="inline w-6 h-6" />
                    )}
                  </div>
                </div>
              </div>
            ),
          },
          {
            id: 'Balance',
            Header: shouldUseModelBalances ? 'Model Balance' : 'Balance',
            accessor: (
              { aggr_balance, currency_prefix, ...balance },
              rowIndex,
            ) => (
              <div>
                <p>
                  {currency_prefix} {curr(aggr_balance)}
                </p>
                <p className="text-xs text-gray-500">
                  {currency_prefix === currency
                    ? ''
                    : currency + ' ' + curr(balance[key], null, true)}
                </p>
              </div>
            ),
          },
          {
            Header: 'Change',
            accessor: ({ aggr_balance_change, currency_prefix }, rowIndex) => (
              <div className="flex items-center h-full">
                {aggr_balance_change === 0 ? (
                  ''
                ) : aggr_balance_change > 0 ? (
                  <span className="text-xs text-green-600 truncate">
                    <HeroIconTrendingUp className="inline w-3 pb-px mr-1" />
                    {currency_prefix + ' ' + curr(aggr_balance_change)}
                  </span>
                ) : (
                  <span className="text-xs text-red-600 truncate">
                    <HeroIconTrendingDown className="inline w-3 pb-px mr-1" />
                    {currency_prefix +
                      ' ' +
                      curr(aggr_balance_change).replace('-', '')}
                  </span>
                )}
              </div>
            ),
          },
          {
            Header: 'Interest',
            accessor: (
              { aggr_balance, aggr_yield, currency_prefix },
              rowIndex,
            ) => {
              const aggr_yield_pct =
                typeof aggr_yield !== 'number' ||
                typeof aggr_balance !== 'number' ||
                aggr_yield + aggr_balance === 0
                  ? 0
                  : (aggr_yield / aggr_balance) * 100;

              return (
                <div className="text-xs">
                  <p>
                    {aggr_yield_pct === 0
                      ? ''
                      : aggr_yield_pct.toFixed(4).replace(/^0.0000$/, '') +
                        '% AER'}
                  </p>
                  <p
                    className={
                      aggr_yield < 0 ? 'text-red-600' : 'text-gray-500'
                    }
                  >
                    {aggr_yield < 0 && '('}
                    {aggr_yield === 0
                      ? ''
                      : currency_prefix +
                        ' ' +
                        curr(aggr_yield).replace('-', '') +
                        '/yr'}
                    {aggr_yield < 0 && ')'}
                  </p>
                </div>
              );
            },
          },
        ]}
        renderExpandableRow={({ row }) => {
          if (state.context.rowIdOpen !== row.id) return null;
          return (
            <CurrencyBookExpandableArea
              key={row.id}
              convert={convert}
              reportModelBalanceChanged={reportModelBalanceChanged}
              currencyBookId={row.id}
            />
          );
        }}
      />
      {(isLimitingCurrencyBooks ||
        typeof currencyBookSelectChoice === 'number' ||
        !currencyBookSelectChoice ||
        businessUnitSelectChoice) && (
        <div className="flex justify-start mt-1 ml-1 text-sm text-gray-600 cursor-default select-none">
          Table has been filtered
        </div>
      )}
    </>
  );
};

export default TableCurrencyBooks;

interface CurrencyBookExpandableAreaProps {
  currencyBookId: number;
  convert: ConvertFunction;
  reportModelBalanceChanged: () => void;
}

const CurrencyBookExpandableArea: React.FC<CurrencyBookExpandableAreaProps> = (
  props,
) => {
  const [result] = useGetCurrencyBookBankAccountsQuery({
    variables: {
      currencyBookId: props.currencyBookId,
    },
  });

  const updateModelBalances = useWrappedUrqlMutation(
    useUpdateModelBalancesMutation,
  );

  const deleteModelBalances = useWrappedUrqlMutation(
    useDeleteModelBalancesMutation,
  );

  const [state, dispatch] = useMachine(updateModelBalancesMachine, {
    actions: {
      onError: () => {
        toast.error(
          'An unknown error occurred: could not update model balances.',
        );
      },
      onSuccess: () => {
        // Unsure if we want to show a success message here...
        // toast('Model balances updated successfully.');
        props.reportModelBalanceChanged();
      },
    },
    services: {
      deleteModelBalances: async () => {
        return deleteModelBalances.dispatch({
          currencyBookId: props.currencyBookId,
        });
      },
      saveModelBalances: async (context) => {
        const balances = Object.entries(context.balanceChanges).map(
          ([bankAccountId, { change, actualBalance }]) => {
            const newBalance = actualBalance + Number(change);
            return {
              bank_account_id: bankAccountId,
              balance: newBalance,
              balance_gbp: props.convert({
                base: result?.data?.bank_accounts?.[0].currency?.prefix as any,
                amount: newBalance,
                target: 'GBP',
              }),
            };
          },
        );
        return updateModelBalances.dispatch({
          balances,
        });
      },
    },
  });

  const { onColumnClick, sortingState, sortedData } = useClientSideSortedTable(
    result?.data?.bank_accounts || [],
    {
      defaultOrderBy: 'Balance',
      sort: (a, b, sortingState) => {
        switch (sortingState.columnId) {
          case 'Account': {
            if (sortingState.order === 'asc') {
              return b.account_id.localeCompare(a.account_id);
            } else {
              return a.account_id.localeCompare(b.account_id);
            }
          }
          case 'Bank': {
            if (sortingState.order === 'asc') {
              return b.bank.name.localeCompare(a.bank.name);
            } else {
              return a.bank.name.localeCompare(b.bank.name);
            }
          }
          case 'Type': {
            if (sortingState.order === 'asc') {
              return b.product_type.localeCompare(a.product_type);
            } else {
              return a.product_type.localeCompare(b.product_type);
            }
          }
          case 'Balance': {
            if (!a.actual_balances?.[0]?.balance) {
              return 1;
            } else if (!b.actual_balances?.[0]?.balance) {
              return -1;
            }
            if (sortingState.order === 'asc') {
              return (
                a.actual_balances?.[0]?.balance -
                b.actual_balances?.[0]?.balance
              );
            } else {
              return (
                b.actual_balances?.[0]?.balance -
                a.actual_balances?.[0]?.balance
              );
            }
          }
          case 'Rate': {
            if (sortingState.order === 'asc') {
              return (
                (a.actual_balances?.[0]?.interest_rate ||
                  a?.opening_interest_rate) -
                (b.actual_balances?.[0]?.interest_rate ||
                  b?.opening_interest_rate)
              );
            } else {
              return (
                (b.actual_balances?.[0]?.interest_rate ||
                  b?.opening_interest_rate) -
                (a.actual_balances?.[0]?.interest_rate ||
                  a?.opening_interest_rate)
              );
            }
          }
          default:
            return 0;
        }
      },
    },
  );

  const [paginatedBankAccounts, pagination] = useClientSidePagination(
    sortedData,
    {
      resultsPerPage: 7,
    },
  );

  const total_change = result?.data?.bank_accounts.reduce((total, account) => {
    return (
      total + Number(getChangeValue(account, state.context.balanceChanges))
    );
  }, 0);

  const currency_prefix = result?.data?.bank_accounts?.[0].currency?.prefix;

  return (
    <div className="px-2 py-2 border-b border-gray-200">
      <div className="mb-2 rounded-lg">
        <Table
          onColumnClick={onColumnClick}
          sortingState={sortingState}
          disabledSortColumns={['Model Balance', 'Proposed ±Change']}
          data={sortedData}
          gridTemplateColumns="190px 60px 65px 1fr 1fr 1fr"
          columns={[
            {
              Header: 'Account',
              accessor: (account) => (
                <div className="flex flex-col py-1 text-xs">
                  <div>{account.bank?.name}</div>
                  <div className="text-gray-500 text-2xs">
                    {account.account_id}
                  </div>
                </div>
              ),
            },
            {
              Header: 'Type',
              accessor: (account) => {
                const fixedMaturityDate = dayjs(
                  account?.fixed_maturity_date,
                ).format('YYYY-MM-DD');

                return (
                  <div className="text-xs">
                    <p>{account.product_type}</p>
                    {account?.product_type === 'NOTICE' && (
                      <p className="text-2xs">{account?.notice_days}D</p>
                    )}
                    {account?.product_type === 'FIXED' && (
                      <p className="text-2xs">{fixedMaturityDate}</p>
                    )}
                  </div>
                );
              },
            },
            {
              Header: 'Rate',
              accessor: (account) => (
                <div className="text-xs">
                  {account?.actual_balances?.[0]?.interest_rate ||
                    account?.opening_interest_rate ||
                    0}
                  %
                </div>
              ),
            },
            {
              Header: `Balance (${currency_prefix})`,
              id: 'Balance',
              accessor: (account) => (
                <div className="mr-2 text-xs break-any">
                  {curr(account?.actual_balances?.[0]?.balance || 0)}
                </div>
              ),
            },
            {
              Header: `Model Balance (${currency_prefix})`,
              id: 'Model Balance',
              accessor: (account) => {
                const actualBalance =
                  account?.actual_balances?.[0]?.balance || 0;
                const change =
                  state.context.balanceChanges?.[account.id]?.change;

                const modelBalance = change
                  ? Number(change) + Number(actualBalance)
                  : account?.model_balances?.[0]?.balance;

                return (
                  <div className="flex items-center justify-end w-full h-full pl-2 text-xs text-gray-700 border-l border-gray-300 border-dashed break-any">
                    {curr(
                      isNaN(modelBalance ?? actualBalance)
                        ? 0
                        : modelBalance ?? actualBalance,
                    )}
                  </div>
                );
              },
            },
            {
              Header: 'Proposed ±Change',
              accessor: (account) => {
                const actualBalance =
                  account?.actual_balances?.[0]?.balance || 0;

                return (
                  <div className="flex items-center p-0 overflow-hidden">
                    <input
                      className="w-full px-2 py-1 text-xs bg-white border border-gray-300 rounded"
                      type="number"
                      value={getChangeValue(
                        account,
                        state.context.balanceChanges,
                      )}
                      onChange={(e) =>
                        dispatch({
                          type: 'EDIT_BALANCE',
                          bankAccountId: account.id,
                          actualBalance: actualBalance,
                          change: e.target.value,
                        })
                      }
                    />
                  </div>
                );
              },
            },
          ]}
          summaryRow={
            <TableRow
              gridTemplateColumns="190px 60px 65px 1fr 1fr 1fr"
              className="py-3 bg-gray-100 border-t border-gray-300 rounded-b-lg"
            >
              <TableCell>
                <p className="text-xs font-semibold text-gray-600 uppercase">
                  Total {pagination.totalPages > 1 ? `(all results)` : ''}
                </p>
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell>
                <div className="text-xs">
                  {curr(
                    result.data?.bank_accounts?.reduce((total, account) => {
                      return (
                        total + (account.actual_balances?.[0]?.balance || 0)
                      );
                    }, 0),
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="w-full text-xs text-right break-any">
                  {curr(
                    result.data?.bank_accounts?.reduce((total, account) => {
                      return (
                        total + (account.actual_balances?.[0]?.balance || 0)
                      );
                    }, 0) +
                      result?.data?.bank_accounts.reduce((total, account) => {
                        return (
                          total +
                          Number(
                            getChangeValue(
                              account,
                              state.context.balanceChanges,
                            ),
                          )
                        );
                      }, 0),
                  )}
                </div>
              </TableCell>
              {state.matches('errored') ? (
                <TableCell className="col-span-2">
                  {state.matches('errored.aBalanceIsNotANumber') && (
                    <p className="text-xs text-red-600">
                      All changes must be numeric
                    </p>
                  )}
                </TableCell>
              ) : (
                <TableCell>
                  <div className="w-full pl-2 ml-px text-xs font-semibold">
                    {total_change > 0 && '+'}
                    {curr(total_change)}
                    <span className="ml-1 font-normal text-gray-500 text-2xs">
                      (NET)
                    </span>
                  </div>
                </TableCell>
              )}
            </TableRow>
          }
        />
      </div>
      <div className="flex justify-end w-full">
        <button
          className="py-1 mr-1 text-red-700 btn btn-small btn-white"
          onClick={() =>
            confirm(
              'Are you sure you want to reset all Proposed Changes for this currency book to 0?',
            ) &&
            dispatch({
              type: 'CLEAR_CHANGES',
            })
          }
        >
          <HeroIconTrash className="inline w-4 h-4 mb-1" /> Clear Changes
        </button>
        <button
          className="py-1 btn btn-small"
          onClick={() =>
            dispatch({
              type: 'SAVE_CHANGES',
            })
          }
        >
          <HeroIconUpload className="inline w-4 h-4 mb-1" /> Save Changes
        </button>
      </div>
    </div>
  );
};

const getChangeValue = (
  account: GetCurrencyBookBankAccountsQuery['bank_accounts'][0],
  balanceChanges: {
    [id: string]: { change: string };
  },
) => {
  const modelBalance = account?.model_balances?.[0]?.balance;
  const actualBalance = account?.actual_balances?.[0]?.balance ?? 0;

  const change = balanceChanges?.[account.id]?.change; // some unsaved input

  const initialChange =
    // typeof actualBalance !== 'undefined' &&
    typeof modelBalance !== 'undefined' && !isNaN(modelBalance)
      ? (Number(modelBalance) - Number(actualBalance)).toFixed(2)
      : 0;

  return change ?? initialChange; // return unsaved input if it's not null or undefined
};
